import { findSubstationBaseEvaluateCode } from './substationBaseEvaluateCodesMapping';
import type { FoulBuildUpRowData } from '../types/tender.types';

export type SubstationBaseRowType = 'sectionHeader' | 'data';

export interface SubstationBaseRowDef {
  id: string;
  itemNo: number | null;
  rowType: SubstationBaseRowType;
  description: string;
  unit: string;
}

export const SUBSTATION_BASE_ROW_DEFS: SubstationBaseRowDef[] = [
  // ── Sub Station Base ─────────────────────────────────────────────────────
  { id: 'ssb_hdr', rowType: 'sectionHeader', itemNo: null, description: 'Sub Station Base',                                                unit: '' },
  { id: 'ssb_1',  rowType: 'data', itemNo: 1,  description: 'Setting out Engineer',                                                       unit: 'hrs' },
  { id: 'ssb_2',  rowType: 'data', itemNo: 2,  description: 'Excavate to formation for foundation',                                        unit: 'm3' },
  { id: 'ssb_3',  rowType: 'data', itemNo: 3,  description: 'Cart Spoil to onsite bund NE 800m',                                          unit: 'm3' },
  { id: 'ssb_4',  rowType: 'data', itemNo: 4,  description: '50mm concrete Blinding',                                                     unit: 'm2' },
  { id: 'ssb_5',  rowType: 'data', itemNo: 5,  description: 'Formwork NE 1.2m to external perimeter',                                     unit: 'm2' },
  { id: 'ssb_6',  rowType: 'data', itemNo: 6,  description: 'Formwork NE 0.85m to form pit to frontage transformer',                      unit: 'm2' },
  { id: 'ssb_7',  rowType: 'data', itemNo: 7,  description: 'Formwork NE 0.9m to form walls void area under floor',                       unit: 'm2' },
  { id: 'ssb_8',  rowType: 'data', itemNo: 8,  description: 'Forwork to Soffit of Void area',                                             unit: 'm2' },
  { id: 'ssb_9',  rowType: 'data', itemNo: 9,  description: 'Supply & Fix 14 sheets A393 Mesh',                                           unit: 'm2' },
  { id: 'ssb_10', rowType: 'data', itemNo: 10, description: 'Supply and Fix 400m R10 Steel for use as L Bars as sketch',                  unit: 't' },
  { id: 'ssb_11', rowType: 'data', itemNo: 11, description: 'Mass Concrete to Sub-Station Base',                                          unit: 'm3' },
  { id: 'ssb_12', rowType: 'data', itemNo: 12, description: 'Backfill to Working Space',                                                  unit: 'm3' },
  { id: 'ssb_13', rowType: 'data', itemNo: 13, description: 'Form 50 x 50 Chamfer to perimeter',                                         unit: 'm' },
  { id: 'ssb_14', rowType: 'data', itemNo: 14, description: 'Form 50 x 50 Rebate to drawpit',                                             unit: 'm' },
  { id: 'ssb_15', rowType: 'data', itemNo: 15, description: 'Supply and Fit 10nr 125mm Ducting from Drawpit out',                         unit: 'm' },
  { id: 'ssb_16', rowType: 'data', itemNo: 16, description: 'Supply and Fit  4nr 50mm Ducting from Drawpit out',                          unit: 'm' },
  { id: 'ssb_17', rowType: 'data', itemNo: 17, description: 'Supply and Fit  6nr 90 ID mm Fibre Ducting from Drawpit out',                unit: 'm' },
  { id: 'ssb_18', rowType: 'data', itemNo: 18, description: 'Welding to A393 Mesh and earthing rods (subby)',                             unit: 'Item' },
  { id: 'ssb_19', rowType: 'data', itemNo: 19, description: 'Paving to Perimeter (1m wide maintenance zone)',                             unit: 'm2' },

  // ── Sub Station Access ───────────────────────────────────────────────────
  { id: 'ssa_hdr', rowType: 'sectionHeader', itemNo: null, description: 'Sub Station Access',                                              unit: '' },
  { id: 'ssa_1',  rowType: 'data', itemNo: 1,  description: 'Level and compact formation',                                                unit: 'm2' },
  { id: 'ssa_2',  rowType: 'data', itemNo: 2,  description: '100mm AC32 DBM base course',                                                 unit: 'm2' },
  { id: 'ssa_3',  rowType: 'data', itemNo: 3,  description: '60mm AC20 DBM binder course',                                                unit: 'm2' },
  { id: 'ssa_4',  rowType: 'data', itemNo: 4,  description: 'Sealing Grit',                                                               unit: 'm2' },
  { id: 'ssa_5',  rowType: 'data', itemNo: 5,  description: 'Sweep & Tack coat',                                                          unit: 'm2' },
  { id: 'ssa_6',  rowType: 'data', itemNo: 6,  description: '25mm AC6 DBM Surface course',                                                unit: 'm2' },
  { id: 'ssa_7',  rowType: 'data', itemNo: 7,  description: '150 x 50mm pcc edgings',                                                    unit: 'm' },
];

/** Sums Substation Bill Summary's Amount (Σ qty × item-library rate resolved by each
 * row's description, live — never from a persisted evaluateCode, see SubstationBillSummary.tsx)
 * — shared by that bill's footer and `useRoadsAndSewersTotalSync.ts`'s `substationTotal`. */
export const sumSubstationAmount = (
  savedData: Record<string, FoulBuildUpRowData>,
  getRate: (code: string) => number | undefined,
): number => {
  let total = 0;
  SUBSTATION_BASE_ROW_DEFS.forEach((row) => {
    if (row.rowType !== 'data') return;
    const qty = savedData[row.id]?.quant ?? 0;
    if (!qty) return;
    const code = findSubstationBaseEvaluateCode(row.description);
    if (!code) return;
    const rate = getRate(code);
    if (rate == null) return;
    total += qty * rate;
  });
  return total;
};

export const SUBSTATION_SECTION_ROW_IDS: Record<string, string[]> = {
  'SUB STATION BASE': [
    'ssb_1','ssb_2','ssb_3','ssb_4','ssb_5','ssb_6','ssb_7','ssb_8','ssb_9',
    'ssb_10','ssb_11','ssb_12','ssb_13','ssb_14','ssb_15','ssb_16','ssb_17','ssb_18','ssb_19',
  ],
  'SUB STATION ACCESS': ['ssa_1','ssa_2','ssa_3','ssa_4','ssa_5','ssa_6','ssa_7'],
};
